import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowUpRight,
  ChevronDown,
  Leaf,
  Mail,
  MapPin,
  Menu,
  Phone,
  Scissors,
  Sprout,
  TreePine,
  X,
  Zap,
  Shovel,
  Flower2,
  CheckCircle2,
  ShieldCheck,
} from "lucide-react";
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaXTwitter,
} from "react-icons/fa6";

const services = [
  { label: "Landscaping Design & Build", icon: Leaf, href: "/landscaping-design-build" },
  { label: "Hydroseeding", icon: Zap, href: "/hydroseeding" },
  { label: "Washing & Water Blasting", icon: Sprout, href: "/washing-water-blasting" },
  { label: "Property Maintenance", icon: Flower2, href: "/property-maintenance" },
  { label: "Lawn Maintenance", icon: CheckCircle2, href: "/lawn-maintenance" },
  { label: "Fencing", icon: ShieldCheck, href: "/fencing" },
];

const navLinks = ["Home", "About", "Contact"];
const businessEmail = "Info@thelandscapingchristchurch.co.nz";
const businessAddress = "Christchurch, New Zealand";
const businessMapUrl = "https://maps.app.goo.gl/jpcSNMC3kdKK1b99A?g_st=iw";

const Navbar = () => {
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobileMenu = () => {
    setMobileOpen(false);
    setServicesOpen(false);
  };

  return (
    <header className="fixed left-0 right-0 top-0 z-[100] bg-white text-slate-950 shadow-[0_6px_30px_rgba(15,23,42,0.08)]">
      <div className="border-b border-slate-100">
        <div className="mx-auto flex h-10 max-w-7xl items-center justify-between px-4 text-xs font-medium text-slate-700 md:px-6">
          <div className="hidden items-center gap-6 md:flex">
            <a href={businessMapUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-emerald-700">
              <MapPin size={15} className="text-slate-950" />
              <span>{businessAddress}</span>
            </a>
            <a href={`mailto:${businessEmail}`} className="flex items-center gap-2 hover:text-emerald-700">
              <Mail size={15} className="text-slate-950" />
              <span>{businessEmail}</span>
            </a>
          </div>

          <a href="tel:+64228980029" className="flex items-center gap-2 md:hidden">
            <Phone size={15} />
            <span>+64 228 980 029</span>
          </a>

          <div className="flex items-center gap-5">
            <div className="hidden items-center gap-3 sm:flex">
              <a href="#top" aria-label="X" className="text-slate-950 hover:text-emerald-700">
                <FaXTwitter size={13} />
              </a>
              <a href="#top" aria-label="LinkedIn" className="grid h-5 w-5 place-items-center rounded-full bg-emerald-600 text-white">
                <FaLinkedinIn size={11} />
              </a>
              <a href="#top" aria-label="Instagram" className="text-slate-950 hover:text-emerald-700">
                <FaInstagram size={13} />
              </a>
              <a href="#top" aria-label="Facebook" className="text-slate-950 hover:text-emerald-700">
                <FaFacebookF size={12} />
              </a>
            </div>
            <a href="tel:+64228980029" className="hidden items-center gap-2 hover:text-emerald-700 md:flex">
              <Phone size={15} />
              <span>+64 228 980 029</span>
            </a>
          </div>
        </div>
      </div>

      <nav className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-3">
          <span className="relative grid h-12 w-12 flex-shrink-0 place-items-center rounded-full bg-emerald-950">
            <span className="absolute bottom-2 left-2 h-4 w-8 rounded-t-full bg-emerald-600" />
            <span className="absolute right-2 top-2 h-5 w-5 rotate-45 rounded-sm bg-lime-400" />
            <Leaf className="relative z-10 text-white" size={27} strokeWidth={2.4} />
          </span>
          <span className="leading-none">
            <span className="block text-[22px] font-black tracking-[-0.02em] text-emerald-950 md:text-[26px]">
              Landscaping
            </span>
            <span className="mt-1 block text-[11px] font-extrabold uppercase tracking-[0.22em] text-lime-600 md:text-[12px]">
              Christchurch
            </span>
          </span>
        </Link>

        <div className="hidden items-center gap-8 text-sm font-semibold lg:flex">
          <Link to="/" className="transition hover:text-emerald-700">
            Home
          </Link>
          <Link to="/about" className="transition hover:text-emerald-700">
            About
          </Link>

          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              type="button"
              onClick={() => setServicesOpen((open) => !open)}
              className="flex items-center gap-1.5 transition hover:text-emerald-700"
            >
              Services
              <ChevronDown size={15} className={`transition ${servicesOpen ? "rotate-180" : ""}`} />
            </button>

            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  className="absolute left-1/2 top-full mt-7 w-64 -translate-x-1/2 rounded-xl border border-slate-100 bg-white p-3 shadow-2xl"
                >
                  {services.map((service) => {
                    const Icon = service.icon;
                    return (
                      <Link
                        key={service.label}
                        to={service.href}
                        className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-bold text-slate-700 transition hover:bg-emerald-50 hover:text-emerald-700"
                      >
                        <span className="grid h-9 w-9 place-items-center rounded-lg bg-lime-100 text-emerald-700">
                          <Icon size={18} />
                        </span>
                        {service.label}
                      </Link>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {navLinks.slice(2).map((link) => (
            <Link key={link} to={link === "Contact" ? "/contact" : `#${link.toLowerCase()}`} className="transition hover:text-emerald-700">
              {link}
            </Link>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href="/contact"
            className="inline-flex items-center gap-2 rounded-full bg-green-600 px-7 py-4 text-sm font-black uppercase text-white shadow-[0_10px_24px_rgba(22,163,74,0.28)] transition hover:bg-green-700"
          >
            Get In Touch
            <ArrowUpRight size={17} />
          </a>
        </div>

        <button
          type="button"
          aria-label="Toggle menu"
          onClick={() => setMobileOpen((open) => !open)}
          className="grid h-11 w-11 place-items-center rounded-full border border-slate-200 text-slate-950 lg:hidden"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-slate-100 bg-white lg:hidden"
          >
            <div className="mx-auto max-w-7xl px-4 py-4">
              <div className="grid gap-1 text-sm font-bold">
                {navLinks.slice(0, 2).map((link) => (
                  <Link
                    key={link}
                    to={link === "About" ? "/about" : "/"}
                    onClick={closeMobileMenu}
                    className="rounded-lg px-3 py-3 hover:bg-emerald-50"
                  >
                    {link}
                  </Link>
                ))}

                <button
                  type="button"
                  onClick={() => setServicesOpen((open) => !open)}
                  className="flex items-center justify-between rounded-lg px-3 py-3 text-left hover:bg-emerald-50"
                >
                  Services
                  <ChevronDown size={16} className={`transition ${servicesOpen ? "rotate-180" : ""}`} />
                </button>

                <AnimatePresence>
                  {servicesOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden pl-3"
                    >
                      {services.map((service) => (
                        <Link
                          key={service.label}
                          to={service.href}
                          onClick={closeMobileMenu}
                          className="block rounded-lg px-3 py-2.5 text-slate-600 hover:bg-emerald-50 hover:text-emerald-700"
                        >
                          {service.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {navLinks.slice(2).map((link) => (
                  <Link
                    key={link}
                    to={link === "Contact" ? "/contact" : "/"}
                    onClick={closeMobileMenu}
                    className="rounded-lg px-3 py-3 hover:bg-emerald-50"
                  >
                    {link}
                  </Link>
                ))}
              </div>

              <a
                href="/contact"
                onClick={closeMobileMenu}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-green-600 px-6 py-4 text-sm font-black uppercase text-white"
              >
                Get In Touch
                <ArrowUpRight size={17} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
